/**
 * The side menu (M4 Phase 2; per-persona shapes M5 P1b; rebuilt on the UI foundation in
 * the W-F shell wave, Mahmoud's shell orders of 2026-10-09).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The tree renders from `personaNavigation()` plus the answers the API gives: the one
 * conditional entry (configuration) renders only on the probe's answer, and a surface
 * the server withholds is absent from the DOM, never greyed out. Home and Dashboard are
 * pinned first; the persona tree follows.
 *
 * THE FRAME (this file owns the behaviour, shell.css the geometry):
 * - The menu is its own scroll container inside the 100vh frame; the screen beside it
 *   scrolls on its own. Neither moves the other.
 * - Collapsed (the icon rail), each entry names itself in a tooltip on hover or focus.
 * - Collapsed, a pointer that DWELLS on the rail opens it as an overlay over the screen:
 *   nothing is pushed aside, and the pointer leaving collapses it again. Keyboard focus
 *   entering the rail opens it at once; focus leaving or Escape closes it.
 * - On a phone the menu is a drawer: the shell's top bar opens it, and the close button,
 *   Escape, the scrim or choosing an entry closes it.
 */

import {
  BadgeCheck,
  Binoculars,
  BellRing,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  CircleHelp,
  ClipboardCheck,
  ClipboardList,
  DraftingCompass,
  Flag,
  FlagTriangleRight,
  Gauge,
  HardHat,
  House,
  IdCard,
  Inbox,
  LayoutDashboard,
  Library,
  ListTodo,
  Mailbox,
  MessageSquareReply,
  MessagesSquare,
  PackageCheck,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  SearchCheck,
  Settings,
  ShieldCheck,
  SquareCheckBig,
  UserRound,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { strings } from "../brand/strings";
import { PersonaBar } from "./PersonaBar";
import { PLANNING_CANDIDATES_ROUTE, personaNavigation, resolvePersonaShape } from "./personaNav";
import type { NavEntry } from "./navModel";
import { QUEUES } from "./queues";
import type { ShellLayout } from "./useShellLayout";

export interface SidebarProps {
  readonly configurationAllowed: boolean;
  /** Whether the principal holds AUDIT_LOG READ; defaults to configurationAllowed in unit fixtures. */
  readonly auditAllowed?: boolean;
  /** The server-derived persona from /api/me; null falls back to the capability probe. */
  readonly workflowClass?: string | null;
  /** The shell's sign-out flow; the persona bar ends the session before it re-authenticates. */
  readonly onSwitchPersona: () => void;
  /** The person's persisted choice: the 56px icon rail. State lives in the shell. */
  readonly rail: boolean;
  /** The shell's rail toggle; the control itself is an icon inside the side menu (M5 P5d). */
  readonly onToggleRail: () => void;
  /** Which frame the shell draws (useShellLayout). Desktop when absent. */
  readonly layout?: ShellLayout;
  /** Phone only: whether the drawer is open. */
  readonly drawerOpen?: boolean;
  /** Phone only: close the drawer (close button, Escape, an entry chosen). */
  readonly onCloseDrawer?: () => void;
  /** The caller's open task count, badged on the Tasks entry (LA W1.3). Null: unknown. */
  readonly taskCount?: number | null;
}

/**
 * One lucide glyph per station, chosen for what the station IS, exported so the icon
 * audit is testable: every route any persona can see has its own icon, and no two
 * stations share one (personaNav.test.tsx).
 */
export const RAIL_ICONS: Record<string, LucideIcon> = {
  "/": House, // the persona landing
  "/dashboard": LayoutDashboard, // the analytics board
  "/calendar": CalendarDays, // the month and year calendar
  "/itps": ClipboardList, // inspection and test plans: a plan of numbered checks
  "/inspections": SearchCheck, // inspections and RFIs: look, then confirm
  "/findings": Flag, // findings and punch lists: the marker left on the work
  "/actions": Wrench, // non-conformities and actions: the corrective fix
  "/schedules": CalendarClock, // schedules: dated, time-bound plans
  "/lessons": BookOpen, // quality lessons learned: the record to read
  "/evaluations": Gauge, // contractor evaluations: a measured score
  "/portfolio/projects": BriefcaseBusiness, // projects and contracts
  "/portfolio/parties": Building2, // parties and contractors: the companies
  "/programs/engineering": DraftingCompass, // engineering surveillance: design work
  "/programs/procurement": PackageCheck, // procurement surveillance: goods checked
  "/programs/construction": HardHat, // construction surveillance: the site
  "/programs/external-audits": ShieldCheck, // external audits: assurance
  "/configuration": Settings, // configuration
  "/guide": CircleHelp, // the guide: help
  // Wave W1: the inbox, the queues (each a register pre-filtered, so each its own glyph),
  // the planning candidates and the witness docket.
  "/tasks": Inbox, // tasks and inbox: what arrives for me
  [QUEUES.itpReview.route]: ClipboardCheck, // plans to review: the plan, checked
  [QUEUES.scheduleSign.route]: CalendarCheck, // schedules to sign: the plan, signed
  [QUEUES.resultApproval.route]: BadgeCheck, // results to approve: the seal
  [QUEUES.myInspections.route]: ListTodo, // my open inspections: my to-do list
  [QUEUES.rectifications.route]: SquareCheckBig, // rectifications to verify: tick it off
  [QUEUES.responsesToDecide.route]: MessagesSquare, // responses to decide: the exchange
  [QUEUES.responseStep.route]: MessageSquareReply, // findings to answer: the reply
  [QUEUES.openFindings.route]: FlagTriangleRight, // open findings: the flags still up
  [PLANNING_CANDIDATES_ROUTE]: CalendarPlus, // planning candidates: dates still to add
  "/docket": Binoculars, // the witness docket: what the third party comes to watch
  // Wave W2: the RFI register. R12's own image: the RFI is the hold and witness doorbell.
  "/rfis": BellRing, // RFIs: the contractor rings for the operator to attend
  // Wave b12 frontend: the account, the outboxes, the standards library, the credentials.
  "/account": UserRound, // your account and preferences: the person
  "/outbox": Mailbox, // the email and webhook outboxes: what was sent
  "/standards": Library, // the standards library: the books the records cite
  "/credentials": IdCard, // credentials: the qualification card
  "/audit": ScrollText, // the audit register: the hash-chained ledger
};

/** A menu target in its three parts: the route, its query parameters, its hash. */
function splitTarget(to: string): { path: string; search: URLSearchParams; hash: string } {
  const hashAt = to.indexOf("#");
  const beforeHash = hashAt >= 0 ? to.slice(0, hashAt) : to;
  const hash = hashAt >= 0 ? to.slice(hashAt) : "";
  const queryAt = beforeHash.indexOf("?");
  return {
    path: queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash,
    search: new URLSearchParams(queryAt >= 0 ? beforeHash.slice(queryAt + 1) : ""),
    hash,
  };
}

/**
 * Which entry is the current page (wave W1). A queue entry (a register with a filter in
 * its query, or a section in its hash) is current only when the page carries its
 * filter; otherwise the plain entry whose route the page sits under is current. So the
 * queue and the whole register never light up together.
 */
export function activeTarget(
  targets: readonly string[],
  location: { readonly pathname: string; readonly search: string; readonly hash: string },
): string | null {
  const here = new URLSearchParams(location.search);
  const qualified = targets.find((to) => {
    const target = splitTarget(to);
    if (target.search.size === 0 && target.hash === "") {
      return false;
    }
    if (target.path !== location.pathname) {
      return false;
    }
    if (target.hash !== "" && target.hash !== location.hash) {
      return false;
    }
    return [...target.search.entries()].every(([name, value]) => here.get(name) === value);
  });
  if (qualified !== undefined) {
    return qualified;
  }
  let best: string | null = null;
  for (const to of targets) {
    const target = splitTarget(to);
    if (target.search.size > 0 || target.hash !== "") {
      continue;
    }
    const under =
      target.path === "/"
        ? location.pathname === "/"
        : location.pathname === target.path || location.pathname.startsWith(`${target.path}/`);
    if (under && (best === null || target.path.length > best.length)) {
      best = target.path;
    }
  }
  return best;
}

/**
 * The pinned Home entry (M6 Station 2b): the persona landing with the open tasks, first
 * in every side menu whatever the persona shape.
 */
export const HOME_ENTRY: NavEntry = { label: strings.nav.home, to: "/" };

/**
 * Mahmoud's order (2026-10-09, ruling 4 extra): the Dashboard entry sits directly AFTER
 * Home at the top of every side menu, whatever the persona shape.
 */
export const DASHBOARD_ENTRY: NavEntry = { label: strings.nav.dashboard, to: "/dashboard" };

/** How long a pointer rests on the rail before it opens as an overlay. The tooltips
 * answer a glance well before this; the overlay answers a dwell. */
export const PEEK_DELAY_MS = 600;

/** How long a pointer rests on a rail icon before its tooltip names it. */
export const TOOLTIP_DELAY_MS = 120;

const ICON_STROKE = 1.75;

/**
 * A tooltip that only ever opens while the menu is the collapsed rail.
 *
 * W1.5 (Mahmoud's walk, finding 1): while the overlay is open the tooltips are off, and
 * Radix still reported "open" for every entry the pointer or the keyboard crossed, but
 * never the matching "closed" (the controlled value was already false). Each remembered
 * open came back the moment the overlay closed and floated there until re-hovered. So an
 * open is only ever RECORDED while the tooltip is enabled, and turning it off forgets it.
 */
function NavTooltip({
  label,
  enabled,
  children,
}: {
  label: string;
  enabled: boolean;
  children: React.ReactElement;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [wasEnabled, setWasEnabled] = useState(enabled);
  if (wasEnabled !== enabled) {
    // Adjusting state while rendering (React's documented pattern, no effect needed).
    setWasEnabled(enabled);
    if (!enabled) {
      setOpen(false);
    }
  }
  return (
    <Tooltip
      open={enabled && open}
      onOpenChange={(next) => {
        setOpen(enabled && next);
      }}
    >
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" data-testid="nav-tooltip">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function Entry({
  entry,
  rail,
  tooltip,
  active,
  badge,
  onNavigate,
}: {
  entry: NavEntry;
  rail: boolean;
  tooltip: boolean;
  active: boolean;
  badge?: number | null | undefined;
  onNavigate: () => void;
}): React.JSX.Element {
  const Icon = RAIL_ICONS[entry.to] ?? House;
  const counted = badge !== null && badge !== undefined && badge > 0;
  return (
    <NavTooltip label={entry.label} enabled={tooltip}>
      <Link
        to={entry.to}
        className={cn("q-sidebar__link", active && "active")}
        aria-current={active ? "page" : undefined}
        aria-label={rail ? entry.label : undefined}
        onClick={onNavigate}
      >
        <Icon className="q-sidebar__icon" aria-hidden="true" strokeWidth={ICON_STROKE} />
        <span className="q-sidebar__label">
          {entry.label}
          {counted && (
            <>
              <span
                className="ms-2 inline-block min-w-5 rounded-full bg-gold-wash px-1.5 text-center text-xs tabular-nums text-gold"
                aria-hidden="true"
                data-testid="nav-task-badge"
              >
                {String(badge)}
              </span>
              <span className="sr-only">{strings.nav.tasksOpen(badge)}</span>
            </>
          )}
        </span>
      </Link>
    </NavTooltip>
  );
}

export function Sidebar({
  configurationAllowed,
  auditAllowed = configurationAllowed,
  workflowClass = null,
  onSwitchPersona,
  rail,
  onToggleRail,
  layout = "desktop",
  drawerOpen = false,
  onCloseDrawer,
  taskCount = null,
}: SidebarProps): React.JSX.Element {
  const location = useLocation();
  const groups = personaNavigation(
    resolvePersonaShape({ configurationAllowed, auditAllowed, workflowClass }),
  )
    .map((group) => ({
      ...group,
      entries: group.entries.filter(
        (entry) =>
          entry.to !== DASHBOARD_ENTRY.to &&
          (entry.when !== "configurationProbe" || configurationAllowed) &&
          (entry.when !== "auditProbe" || auditAllowed),
      ),
    }))
    .filter((group) => group.entries.length > 0);
  const current = activeTarget(
    [HOME_ENTRY.to, DASHBOARD_ENTRY.to, ...groups.flatMap((g) => g.entries.map((e) => e.to))],
    location,
  );

  const phone = layout === "phone";
  // The tablet frame is always the rail; the desktop frame is the person's choice.
  const railShown = !phone && (rail || layout === "tablet");
  const [peekState, setPeek] = useState(false);
  const peek = railShown && peekState;
  const collapsed = railShown && !peek;

  const navRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const peekTimer = useRef<number | undefined>(undefined);
  const pointerDown = useRef(false);

  function cancelPeekTimer(): void {
    if (peekTimer.current !== undefined) {
      window.clearTimeout(peekTimer.current);
      peekTimer.current = undefined;
    }
  }

  // No timer outlives the menu.
  useEffect(() => cancelPeekTimer, []);

  // An overlay opened by a tap (touch has no pointer-leave) closes on a tap elsewhere.
  useEffect(() => {
    if (!peek) {
      return undefined;
    }
    function onPointerDown(event: PointerEvent): void {
      if (!(event.target instanceof Node) || navRef.current?.contains(event.target) !== true) {
        setPeek(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [peek]);

  // The drawer takes focus when it opens, on its own close button.
  useEffect(() => {
    if (phone && drawerOpen) {
      closeRef.current?.focus();
    }
  }, [phone, drawerOpen]);

  function onNavigate(): void {
    if (phone && drawerOpen) {
      onCloseDrawer?.();
    }
  }

  function onToggle(): void {
    if (layout === "tablet") {
      // The tablet rail never pushes the screen: the toggle opens the overlay instead.
      cancelPeekTimer();
      setPeek((value) => !value);
      return;
    }
    setPeek(false);
    onToggleRail();
  }

  const toggleLabel =
    layout === "tablet"
      ? peek
        ? strings.nav.railCollapse
        : strings.nav.railExpand
      : rail
        ? strings.nav.railExpand
        : strings.nav.railCollapse;
  const toggleExpanded = layout === "tablet" ? peek : !rail;
  const ToggleIcon = toggleExpanded ? PanelLeftClose : PanelLeftOpen;

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS} skipDelayDuration={300}>
      <nav
        ref={navRef}
        className={cn(
          "q-sidebar",
          railShown && "q-sidebar--rail",
          peek && "q-sidebar--peek",
          phone && "q-sidebar--drawer",
          phone && drawerOpen && "q-sidebar--open",
        )}
        aria-label={strings.nav.primary}
        data-testid="sidebar"
        onPointerDown={() => {
          pointerDown.current = true;
        }}
        onPointerUp={() => {
          pointerDown.current = false;
        }}
        onPointerEnter={(event) => {
          if (!railShown || event.pointerType === "touch") {
            return;
          }
          cancelPeekTimer();
          peekTimer.current = window.setTimeout(() => {
            peekTimer.current = undefined;
            setPeek(true);
          }, PEEK_DELAY_MS);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "touch") {
            return;
          }
          cancelPeekTimer();
          setPeek(false);
        }}
        onFocus={() => {
          // Keyboard focus opens the overlay at once; a click's focus waits for the dwell.
          if (railShown && !pointerDown.current) {
            cancelPeekTimer();
            setPeek(true);
          }
        }}
        onBlur={(event) => {
          const next = event.relatedTarget;
          if (!(next instanceof Node) || navRef.current?.contains(next) !== true) {
            cancelPeekTimer();
            setPeek(false);
          }
        }}
        onKeyDown={(event) => {
          if (event.key !== "Escape") {
            return;
          }
          if (phone && drawerOpen) {
            onCloseDrawer?.();
          } else if (peek) {
            setPeek(false);
          }
        }}
      >
        <div className="q-sidebar__panel" id="q-sidebar-panel">
          <div className="q-sidebar__head">
            {phone ? (
              <button
                ref={closeRef}
                type="button"
                className="q-sidebar__control"
                aria-label={strings.nav.drawerClose}
                data-testid="drawer-close"
                onClick={onCloseDrawer}
              >
                <X className="q-sidebar__icon" aria-hidden="true" strokeWidth={ICON_STROKE} />
              </button>
            ) : (
              <NavTooltip label={toggleLabel} enabled>
                <button
                  type="button"
                  className="q-sidebar__control"
                  aria-expanded={toggleExpanded}
                  aria-label={toggleLabel}
                  data-testid="rail-toggle"
                  onClick={onToggle}
                >
                  <ToggleIcon
                    className="q-sidebar__icon"
                    aria-hidden="true"
                    strokeWidth={ICON_STROKE}
                  />
                </button>
              </NavTooltip>
            )}
          </div>
          <div className="q-sidebar__body" id="q-sidebar-body" data-testid="sidebar-body">
            <section className="q-sidebar__group" data-testid="sidebar-home">
              <Entry
                entry={HOME_ENTRY}
                rail={railShown}
                tooltip={collapsed}
                active={current === HOME_ENTRY.to}
                onNavigate={onNavigate}
              />
              <Entry
                entry={DASHBOARD_ENTRY}
                rail={railShown}
                tooltip={collapsed}
                active={current === DASHBOARD_ENTRY.to}
                onNavigate={onNavigate}
              />
            </section>
            {groups.map((group) => (
              <section className="q-sidebar__group" key={group.heading}>
                <h2 className="q-sidebar__heading">{group.heading}</h2>
                {group.entries.map((entry) => (
                  <Entry
                    key={`${entry.to} ${entry.label}`}
                    entry={entry}
                    rail={railShown}
                    tooltip={collapsed}
                    active={current === entry.to}
                    badge={entry.to === "/tasks" ? taskCount : null}
                    onNavigate={onNavigate}
                  />
                ))}
              </section>
            ))}
            <PersonaBar onSwitch={onSwitchPersona} />
          </div>
        </div>
      </nav>
    </TooltipProvider>
  );
}
